import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import JoditEditor, { Jodit } from "jodit-react";
import DOMPurify from "dompurify";
import { fontsWithSizes, buttons } from "./utils";
import { useFormContext } from "../Form";
import { classNames } from "../utils/classNames";
const BASIC_THEME_NAME = "piston";
const BASIC_CLASS_NAME = "jodit-wrapper";
const sanitiseHtml = (value) => DOMPurify.sanitize(value, {
    ADD_ATTR: [
        "clicktracking",
        "opentracking",
        "ganalytics",
        "urlshortener",
        "unsubscribe",
        "template-id",
        "tracking-id",
    ],
});
const setEditorValue = (editor, value, echo) => {
    echo.current = { given: sanitiseHtml(value) };
    editor.value = value;
    echo.current.rendered = editor.value;
};
export const WYSIWYGEditor = observer(({ html, onChange, onBlur, onInit, className, name, customControls = [], fonts = {}, sizes = {}, width = "auto", height = 250, allowTabNavigation = true, disabled = false, useNativeTooltip = false, toolbarAdaptive = false, showPlaceholder = true, }) => {
    const { values } = useFormContext();
    // Freeze the initial value so jodit-react's useEffect([value]) never fires
    // after mount — we manage external updates imperatively via joditRef.
    const initialValueRef = useRef(sanitiseHtml(html));
    // Tracks the latest value the user typed (or the last external value we
    // pushed), so we can restore it after a config-triggered Jodit reinit.
    const lastKnownValueRef = useRef(html);
    // Direct reference to the live Jodit instance, set via afterInit.
    const joditRef = useRef(null);
    // Lets an untouched editor report the value it was given byte-identical,
    // so loading or focusing it doesn't mark the form dirty.
    const echoRef = useRef(null);
    // Track each control's disabled state in a ref so isDisabled() always
    // returns the latest value without forcing a config recreation (and
    // therefore a Jodit reinit + cursor reset) on every disabled change.
    const controlDisabledRef = useRef({});
    // Only changes when controls are added or removed — not when disabled changes.
    const controlNamesKey = customControls.map((c) => c?.name ?? "").join(",");
    const disabledKey = customControls
        .map((c) => `${c?.name}:${c?.disabled}`)
        .join(",");
    useEffect(() => {
        customControls.forEach((control) => {
            if (control) {
                controlDisabledRef.current[control.name] = control.disabled;
            }
        });
    }, [disabledKey]);
    const handleEvent = useCallback((handler, newValue) => {
        if (typeof handler !== "function")
            return;
        const echo = echoRef.current;
        const value = echo && newValue === (echo.rendered ?? newValue)
            ? echo.given
            : newValue;
        // Record before calling handler so lastKnownValueRef is always current
        // when the parent re-renders and html prop propagates back down.
        lastKnownValueRef.current = value;
        handler(value);
        if (name) {
            values.set(name, value);
        }
    }, [name, values]);
    // Push external html changes into Jodit without going through the
    // jodit-react value prop, which resets cursor position. Skip if the
    // user is currently focused — their live content takes precedence.
    useEffect(() => {
        const editor = joditRef.current;
        if (!editor || html === lastKnownValueRef.current) {
            lastKnownValueRef.current = html;
            return;
        }
        if (editor.s?.isFocused?.()) {
            // Keep lastKnownValueRef pointing at the live editor value while focused.
            lastKnownValueRef.current = editor.value;
            return;
        }
        lastKnownValueRef.current = html;
        setEditorValue(editor, html, echoRef);
    }, [html]);
    useEffect(() => {
        joditRef.current?.toolbar?.update?.();
    }, [disabledKey]);
    const menuControls = useMemo(() => {
        return customControls.reduce((o, control) => ({
            ...o,
            [control.name]: {
                name: control.label,
                tooltip: control.tooltip,
                exec: control.onClick,
                isDisabled: () => controlDisabledRef.current[control.name] ?? false,
            },
        }), {});
    }, [controlNamesKey]);
    const config = useMemo(() => ({
        ...Jodit.defaultOptions,
        controls: {
            ...fontsWithSizes(fonts, sizes),
            ...menuControls,
        },
        events: {
            beforeSetValueToEditor: (value) => typeof value === "string" ? sanitiseHtml(value) : undefined,
            beforePasteInsert: sanitiseHtml,
            afterInit: (instance) => {
                joditRef.current = instance;
                // Restore content after a config-triggered reinit; jodit-react
                // reinitialises from the frozen initialValueRef, so we need to
                // write back whatever the user had typed since mount.
                if (instance.value !== lastKnownValueRef.current) {
                    setEditorValue(instance, lastKnownValueRef.current, echoRef);
                }
                if (typeof onInit === "function") {
                    onInit(instance);
                }
            },
        },
        extraButtons: Object.keys(menuControls),
        theme: BASIC_THEME_NAME,
        disablePlugins: "powered-by-jodit",
        buttons,
        disabled,
        width,
        height,
        allowTabNavigation,
        toolbarAdaptive,
        useNativeTooltip,
        showPlaceholder,
    }), [
        menuControls,
        disabled,
        width,
        height,
        allowTabNavigation,
        toolbarAdaptive,
        useNativeTooltip,
    ]);
    const handleChange = useCallback((newValue) => handleEvent(onChange, newValue), [handleEvent, onChange]);
    const handleBlur = useCallback((newValue) => handleEvent(onBlur, newValue), [handleEvent, onBlur]);
    return (React.createElement(JoditEditor, { value: initialValueRef.current, 
        // jodit-react types config as DeepPartial<Config>, which is too deep
        // for tsc to check against this object (TS2589).
        config: config, className: classNames(BASIC_CLASS_NAME, className), onChange: handleChange, onBlur: handleBlur }));
});
